import { KPIDirection, KPIUnit } from "../types";

/**
 * How each KPI is presented: the words around the number, and the two facts
 * needed to format it — `unit` and which direction counts as good.
 *
 * This lives here rather than in the API because it is presentation, not data.
 * The engine returns figures and ledger account codes; this decides how a
 * member is told what they mean, and whether a rising line should read as
 * progress. Rewording a definition is a frontend deploy, not an API change.
 *
 * It also gates the page: a KPI the API returns with no entry here is skipped,
 * so shipping a calculation before its card is written never renders a card
 * with blank prose.
 */
/**
 * Where a KPI card lives, so pages outside the dashboard can send a member to
 * one — the account mapping page links here to show which card a ledger row
 * feeds.
 *
 * The id and the href are built from one place on purpose: the anchor is
 * written on the dashboard and the link is written three modules away, and the
 * two going quietly out of step would leave a link that lands at the top of the
 * page with nothing to say it missed.
 */
export const MEMBER_DASHBOARD_PATH = "/hub/member-dashboard";

export const kpiElementId = (kpiCode: string): string => `kpi-${kpiCode}`;

export const kpiCardHref = (kpiCode: string): string =>
  `${MEMBER_DASHBOARD_PATH}#${kpiElementId(kpiCode)}`;

export interface KPIConfig {
  name: string;
  source: string;
  /** Which block of the page this card belongs to. */
  section: SectionCode;
  unit: KPIUnit;
  direction: KPIDirection;
  definition: string;
  guidance: string;
  /** Reads as the top of the fraction on the card back. */
  numeratorLabel: string;
  /**
   * The bottom of the fraction. Absent for a KPI that is a single figure
   * rather than a ratio — the card back then shows no dividing rule.
   */
  denominatorLabel?: string;
  multiplier?: string;
  /**
   * How the worked example prints the top of the fraction. Money unless
   * stated: a closing ratio divides jobs by jobs, and "$2,092 jobs" reads as
   * nonsense.
   */
  numeratorUnit?: "currency" | "count";
  /**
   * The kind of ServiceTitan work this card measures, as the member sees it on
   * the business unit screen.
   *
   * Only set on section 03's cards, and only read when one of them is
   * unavailable — the message has to name the work type the member is missing,
   * and "service" was hardcoded until Average Replacement Ticket made that a
   * falsehood.
   */
  workTypeLabel?: string;
  /**
   * How the worked example prints the bottom of the fraction. Money unless
   * stated — every KPI before section 03 divided one figure of money by
   * another, and a count rendered as "$42" reads as nonsense.
   */
  denominatorUnit?: "currency" | "count";
  /**
   * What the denominator counts, printed after it: "2,520 service jobs".
   * Only meaningful alongside `denominatorUnit: "count"`.
   */
  denominatorNoun?: string;
  /**
   * How the worked example folds in the period's length, for the two KPIs that
   * scale by it. The labels above stay prose — "× days in period" is the right
   * wording on the card's fraction — but the example needs the number, or it
   * prints a division a member cannot reproduce.
   *
   * - `multiplier` — the period scales the result: `(A ÷ B) × 396`
   * - `denominator` — the period scales the denominator: `A ÷ (B ÷ 396)`
   *
   * Absent for every other KPI, whose example already closes on its own.
   */
  periodScaling?: "multiplier" | "denominator";
  /**
   * True when the numerator is what's left of the denominator after costs
   * come out, as it is for a margin. The worked example then shows the
   * subtraction — "($262K − $155K) ÷ $262K" — instead of a single figure the
   * member would find nowhere in their books.
   */
  numeratorIsRemainder?: boolean;
  note: string;
}

export const KPI_CONFIG: Record<string, KPIConfig> = {
  field_labor_percent: {
    section: "pnl",
    name: "Field labor",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "lower_is_better",
    definition:
      "Of every dollar you bring in, this slice pays the techs and salespeople who do the work out in the field.",
    guidance:
      "Rising usually means crews are staffed heavier than the work coming in, or pay is outrunning sales.",
    numeratorLabel: "Gross wages and burden — field techs + salespeople",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "Every dollar of pay — base, commission, spiffs, and the payroll burden that follows them — for the people who run calls and sell in the field, divided by all the revenue you collected.",
  },
  office_labor_percent: {
    section: "pnl",
    name: "Office labor",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "lower_is_better",
    definition:
      "The share of revenue spent on you and the office team — the people who keep the business running behind the scenes.",
    guidance:
      "If it creeps up over time, office payroll is growing faster than the company is.",
    numeratorLabel: "Gross wages and burden — owner + office team",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "Pay for you and the back-office crew — CSRs, dispatch, admin — plus the payroll burden that follows those wages, divided by all the revenue you collected.",
  },
  gross_margin_percent: {
    section: "pnl",
    name: "Gross margin",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "higher_is_better",
    definition:
      "What's left from each job after you pay for the labor and materials to do it — before overhead.",
    guidance:
      "This is your engine. A steady or rising line means your pricing and job costs are in good shape.",
    numeratorLabel: "Total revenue − direct costs (labor + materials)",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    numeratorIsRemainder: true,
    note: "Start with revenue, then take out the labor and parts it took to do the jobs — what's left is your gross margin. Overhead like office pay, marketing and rent isn't taken out here; that comes off at net profit.",
  },
  net_profit_percent: {
    section: "pnl",
    name: "Net profit",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "higher_is_better",
    definition:
      "The money you actually keep after everything is paid — your true bottom line.",
    guidance:
      "A dip here means costs grew faster than sales somewhere above. Trace it back through the other P&L cards.",
    numeratorLabel: "Gross margin − operating expenses ± other",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "Take your gross margin and subtract everything else it costs to keep the doors open — rent, trucks, insurance, marketing, office pay. Then anything from outside the day-to-day, like interest or a one-off gain, is added or taken off. What's left is your net income, the same figure your QuickBooks P&L ends on.",
  },
  materials_equipment_percent: {
    section: "pnl",
    name: "Materials & equipment",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "lower_is_better",
    definition:
      "The portion of revenue spent on the parts and equipment you install on jobs.",
    guidance:
      "Holding steady is what you want. Spikes can mean rising supplier prices, wasted material, or jobs priced too low for what they cost.",
    numeratorLabel: "Parts and equipment installed on jobs",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "What you paid your suppliers for the materials and equipment that went into the work — divided by all the revenue you collected. Labor to install it isn't in here; that's field labor.",
  },
  accounts_receivable: {
    section: "balance_sheet",
    name: "Accounts receivable",
    source: "QuickBooks · Balance Sheet",
    unit: "currency",
    direction: "lower_is_better",
    definition:
      "Money your customers still owe you for work you've already finished.",
    guidance:
      "Lower and flatter is better. A climbing line is cash stuck in unpaid invoices instead of your bank.",
    numeratorLabel:
      "The closing balance of the accounts you mapped to Accounts Receivable",
    // The second sentence used to assert that undeposited money is excluded.
    // Nothing enforces that: `sumResolvedBalances()` sums whatever the member
    // mapped to Accounts Receivable, and auto-map's suggestion of Other Current
    // Assets for Undeposited Funds is a suggestion they can override. So the
    // card describes the mapping instead of making a claim about their books
    // (BS-6, settled 2026-09-17).
    note: "Every open invoice your customers haven't paid yet, as of the last day of the month. What counts depends on which accounts you mapped to Accounts Receivable.",
  },
  days_sales_outstanding: {
    section: "balance_sheet",
    name: "Days sales outstanding",
    source: "QuickBooks · A/R + Sales",
    unit: "days",
    direction: "lower_is_better",
    definition:
      "How long, on average, it takes to actually collect after a job is done — your A/R balance measured in time.",
    guidance:
      "Creeping up means cash is sitting in unpaid invoices longer. Same story as your A/R balance, measured in time.",
    numeratorLabel: "Accounts receivable",
    denominatorLabel: "Total sales",
    multiplier: "× days in period",
    periodScaling: "multiplier",
    // Deliberately "total sales", not the textbook "total credit sales".
    // QuickBooks' P&L cannot separate work billed on credit from a job paid at
    // the door, so this divides by everything sold — which reads faster than
    // reality, most so for members who collect on completion. Saying "credit
    // sales" would claim a split we did not compute.
    note: "Take what customers still owe you, divide by everything you sold over the period, then multiply by the days in it. Lower means you turn finished work into cash faster.",
  },
  days_cash_on_hand: {
    section: "balance_sheet",
    name: "Days cash on hand",
    // Names its inputs, not the reports they came from — the convention Days
    // Sales Outstanding set with "A/R + Sales" (BS-8, settled 2026-09-17). A
    // member recognises their own cash and their own expenses; "Balance Sheet +
    // P&L" describes our plumbing.
    source: "QuickBooks · Cash + Expenses",
    unit: "days",
    direction: "higher_is_better",
    definition:
      "How many days you could keep paying the bills from cash on hand if no new money came in — your runway.",
    guidance:
      "More days of cash means you can ride out a slow month without scrambling or borrowing.",
    numeratorLabel: "Cash & cash equivalents",
    denominatorLabel: "Operating expenses ÷ days in period",
    periodScaling: "denominator",
    // Depreciation cannot be excluded: our ledger catalog has no account for
    // it, so it sits inside other overhead. The burn rate is therefore a little
    // high and the runway a little short — an error in the safe direction, but
    // the note below does not repeat the prototype's claim that non-cash items
    // are left out, because they are not.
    note: "Cash in your accounts divided by your average daily operating spend. Materials and field labour aren't counted — with no work coming in you'd stop paying those too.",
  },
  retained_earnings: {
    section: "balance_sheet",
    name: "Retained earnings",
    source: "QuickBooks · Balance Sheet",
    unit: "currency",
    direction: "higher_is_better",
    definition:
      "All the profit your company has kept and reinvested since the day it opened.",
    guidance:
      "A steady climb means you're building real, lasting value — not just covering this month's bills.",
    numeratorLabel:
      "All-time net profit kept in the business, minus any owner draws",
    note: "It grows every month you turn a profit and leave that money in the company instead of taking it out. This year's profit counts as it accrues, rather than waiting for the books to close.",
  },
  average_service_ticket: {
    section: "sales_operations",
    name: "Average service ticket",
    source: "ServiceTitan · Jobs",
    unit: "currency",
    direction: "higher_is_better",
    definition: "The average dollar amount of a service or repair visit.",
    guidance:
      "Growth here means techs are solving more of the customer's problem on each visit — not leaving work behind.",
    numeratorLabel: "Total service revenue",
    denominatorLabel: "Number of service jobs",
    workTypeLabel: "Service",
    denominatorUnit: "count",
    denominatorNoun: "service jobs",
    // "completed", not the prototype's "ran". Field Closing Ratio uses *ran*
    // for a different population — a job with a Done appointment — and the two
    // cards sit on the same page.
    note: "Add up all your service and repair revenue, then divide by the number of service jobs you completed. Zero-dollar and no-charge jobs are left out.",
  },
  average_replacement_ticket: {
    section: "sales_operations",
    name: "Average replacement ticket",
    source: "ServiceTitan · Sold estimates",
    unit: "currency",
    direction: "higher_is_better",
    definition:
      "The average dollar amount when you sell and install a brand-new system.",
    guidance:
      "Trending up means your team is selling complete, higher-quality solutions instead of just the cheapest fix.",
    numeratorLabel: "Total sold value, before tax",
    denominatorLabel: "Replacement jobs sold",
    workTypeLabel: "Replacement / Install",
    denominatorUnit: "count",
    denominatorNoun: "systems sold",
    // Counted when sold, not when installed — so the note says "sold", and the
    // source names sold estimates rather than jobs.
    note: "Add up the sold value of every new system, before tax, then divide by the number of new systems you sold. Change orders on a sold job are included in the value.",
  },
  field_closing_ratio: {
    section: "sales_operations",
    name: "Field closing ratio",
    source: "ServiceTitan · Appointments",
    unit: "percent",
    direction: "higher_is_better",
    definition:
      "Out of every 100 service calls your techs run, how many turn into approved, paid work.",
    guidance:
      "Higher means techs are explaining the value well and customers are comfortable saying yes.",
    numeratorLabel: "Service jobs sold",
    denominatorLabel: "Total service jobs run",
    workTypeLabel: "Service",
    multiplier: "× 100",
    // Both halves are job counts, so neither is money.
    numeratorUnit: "count",
    denominatorUnit: "count",
    denominatorNoun: "jobs run",
    note: "Of the service calls your techs ran, the share that ended in approved, paid work. A sale counts when it lands within 30 days of the visit.",
  },
  sales_closing_ratio: {
    section: "sales_operations",
    name: "Sales closing ratio",
    source: "ServiceTitan · Appointments",
    unit: "percent",
    direction: "higher_is_better",
    definition:
      "Out of every 100 sales visits your team runs, how many become a sold job.",
    guidance:
      "It's your fastest lever on revenue — a slipping number usually means a follow-up, pricing, or sales-skill gap.",
    numeratorLabel: "Sales visits that sold",
    denominatorLabel: "Sales visits run",
    multiplier: "× 100",
    numeratorUnit: "count",
    denominatorUnit: "count",
    denominatorNoun: "sales visits",
    workTypeLabel: "Replacement / Install",
    note: "Of the sales visits your team ran, the share that turned into a signed job. A sale counts when it lands within 30 days of the visit.",
  },
  call_center_closing_ratio: {
    section: "sales_operations",
    name: "Call center closing ratio",
    source: "ServiceTitan · Calls",
    unit: "percent",
    direction: "higher_is_better",
    definition:
      "Out of every 100 callers who could have booked, how many your team actually got onto the schedule.",
    guidance:
      "Every missed booking is a lead you already paid for but never got the chance to win.",
    numeratorLabel: "Calls booked",
    denominatorLabel: "Total bookable calls",
    multiplier: "× 100",
    numeratorUnit: "count",
    denominatorUnit: "count",
    denominatorNoun: "bookable calls",
    // No workTypeLabel: this card reads bookable call reasons, not business
    // units, so it can never be unavailable for want of a work type.
    note: "Of the people who called and could have booked, the share your CSRs actually got onto the schedule.",
  },
  debt_to_equity: {
    section: "balance_sheet",
    name: "Debt to equity",
    source: "QuickBooks · Balance Sheet",
    unit: "ratio",
    direction: "lower_is_better",
    definition:
      "How much of the business is funded by borrowing versus your own equity. Below 1.0 means you own more than you owe.",
    guidance:
      "Trending down as you pay debt off — you're funding the company with your own equity, not the bank's. Above 1.0 is the flag you're surviving on debt.",
    numeratorLabel: "Total liabilities",
    denominatorLabel: "Total equity",
    note: "Everything the company owes — loans, lines of credit, financed equipment — divided by owner's equity. Some coaches use only interest-bearing debt for a cleaner read on leverage.",
  },
};

export type SectionCode = "pnl" | "balance_sheet" | "sales_operations";

export interface DashboardSectionConfig {
  title: string;
  source: string;
  blurb: string;
  /**
   * True when the section's cards arrive only for a member who has connected
   * the system behind them. The API omits the whole section otherwise — a
   * member who has never connected ServiceTitan is not missing data, the
   * section simply does not apply to them.
   *
   * So the number of cards on the page is a property of the member, not of
   * this config, and anything that needs the count every member gets has to
   * leave these sections out.
   */
  requiresIntegration?: boolean;
}

/**
 * The page's blocks, in the order they render.
 *
 * A section with no KPIs is not rendered at all, so adding one here ahead of
 * its calculations does not leave an empty heading on the page.
 */
export const SECTIONS: Record<SectionCode, DashboardSectionConfig> = {
  pnl: {
    title: "Profit & Loss",
    source: "QuickBooks",
    blurb:
      "How your money moves through the business each month — what comes in, what it costs to do the work, and what you keep.",
  },
  balance_sheet: {
    title: "Balance Sheet",
    source: "QuickBooks",
    blurb:
      "The bigger picture of the company's financial health — what it owns and owes, and how fast it collects.",
  },
  sales_operations: {
    title: "Sales & Operations",
    source: "ServiceTitan",
    requiresIntegration: true,
    blurb:
      "How well your team turns calls and leads into sold, profitable work in the field.",
  },
};
